import { JSX, useContext } from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const CriticalAlertsDescription = (): JSX.Element => {
  const ffContext = useContext(FeatureFlagContext);
  const veryLowOnly = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_VERY_LOW_LOCATION_CONFIDENCE_ONLY);

  return <>
    <p>
      Critical integrity alerts identify critical issues in the provider network using the combination of advanced analytics and external datasets.
      They are triggered when claims data, state licensure records, or predictive models indicate a provider is unlikely to be practicing at a listed location or specialty or may no longer be active.
    </p>
    <p>
      Critical Alerts:
    </p>
    <ul>
      <li>
        <b>Ghost Providers (Total Claims)</b>: Identifies Ghost providers (providers without claims) using a robust repository of healthcare claims data (70-75% of all claims nationally) to determine whether a provider is active. Applies to both practitioner and facility provider types
      </li>
      <li>
        <b>Ghost Providers (LOB Claims)</b>: Identifies Ghost providers utilizing claims specific to the Medicare, Medicaid, Commercial, or Military lines of business, depending on project configuration
      </li>
      <li>
        <b>Expired License Practitioners</b>: Identifies practitioners where all known MD, DO, and PA state licenses have expired
      </li>
      {veryLowOnly ? (
        <li>
          <b>Very Low Location Confidence</b>: Identifies practitioner-address combinations not likely to be correct, using state-of-the-art machine learning models that analyze hundreds of data attributes relevant to practitioners and service locations across Quest&apos;s proprietary longitudinal datasets and publicly available sources. An outcome of Very Low indicates that a Practitioner is not at the location with 85% confidence or higher.
        </li>
      ) : (
        <>
          <li>
            <b>Low Location Confidence</b>: Identifies practitioner-address combinations not likely to be correct, using state-of-the-art machine learning models that analyze hundreds of data points across Quest&apos;s proprietary longitudinal datasets and publicly available sources
          </li>
          <ul>
            <li>
              <b>Low</b>: Practitioner is NOT at location with 51-85% confidence
            </li>
            <li>
              <b>Very Low</b>: Practitioner IS NOT at location with 85% confidence
            </li>
          </ul>
        </>
      )}
    </ul>
    <p>
      Critical alerts help pinpoint the most urgent and impactful issues that could affect your network&apos;s ability to meet compliance or member experience expectations.
    </p>
    <p>
      Detailed network provider information is available when combined with an active QES® Accuracy or Provider Claims Insights license.
    </p>
  </>;
}

export { CriticalAlertsDescription };
